import lockr from 'lockr';
import constants from '../constants';

const {
    AUTH_KEY,
    LOCALSTORAGE_EXPIRES_KEY, // idle anchor: last user-initiated activity
    LOCALSTORAGE_LOGIN_KEY, // absolute anchor: login time
    LOCALSTORAGE_EXPIRES_TIME, // idle timeout (minutes)
    SESSION_ABSOLUTE_TIMEOUT_HOURS, // absolute cap (hours)
} = constants;

const IDLE_TIMEOUT_MS = LOCALSTORAGE_EXPIRES_TIME * 60 * 1000;
const ABSOLUTE_TIMEOUT_MS = SESSION_ABSOLUTE_TIMEOUT_HOURS * 60 * 60 * 1000;

/**
 * Pure expiry decision so it can be unit-tested without touching storage.
 * A session is expired when either the idle window (time since last activity)
 * or the absolute window (time since login) has been exceeded, or when either
 * anchor is missing.
 */
export const isSessionExpiredAt = (
    nowMs: number,
    lastActivityMs: number | null,
    loginAtMs: number | null,
): boolean => {
    // Without an activity anchor we can't reason about the session at all.
    if (lastActivityMs === null) return true;
    // Legacy sessions created before the absolute-timeout anchor existed have no
    // login timestamp; fall back to the activity anchor so they aren't force
    // logged out on deploy (they get a real login anchor on next sign-in).
    const loginAnchorMs = loginAtMs ?? lastActivityMs;
    if (nowMs - lastActivityMs >= IDLE_TIMEOUT_MS) return true;
    if (nowMs - loginAnchorMs >= ABSOLUTE_TIMEOUT_MS) return true;
    return false;
};

/** Coerce a stored anchor (number, or legacy ISO/Date string) to milliseconds. */
const toMs = (value: unknown): number | null => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
};

/** Record a fresh login: resets both the idle and absolute anchors. */
export const recordLogin = (): void => {
    const now = Date.now();
    lockr.set(LOCALSTORAGE_LOGIN_KEY, now);
    lockr.set(LOCALSTORAGE_EXPIRES_KEY, now);
};

/**
 * Mark user activity: refreshes the idle anchor only. The absolute anchor is
 * intentionally left untouched so the 24h cap cannot be extended by activity.
 */
export const recordActivity = (): void => {
    lockr.set(LOCALSTORAGE_EXPIRES_KEY, Date.now());
};

/** Whether the current stored session has expired (idle or absolute). */
export const isSessionExpired = (): boolean => isSessionExpiredAt(
    Date.now(),
    toMs(lockr.get(LOCALSTORAGE_EXPIRES_KEY)),
    toMs(lockr.get(LOCALSTORAGE_LOGIN_KEY)),
);

/**
 * Remove the auth token and all session anchors from storage.
 *
 * The auth keys are removed explicitly, then `localStorage.clear()` wipes the
 * remainder. The full wipe is intentional: on logout / session expiry we also
 * want the persisted Redux store (redux-persist's `vfm-root`, which holds user
 * data) gone so no stale authenticated state survives. This preserves the
 * app's long-standing logout behaviour. The app does not keep
 * logged-out-user preferences in localStorage that need to survive this.
 */
export const clearSessionAuth = (): void => {
    lockr.rm(AUTH_KEY);
    lockr.rm(LOCALSTORAGE_EXPIRES_KEY);
    lockr.rm(LOCALSTORAGE_LOGIN_KEY);
    lockr.rm('twoFactorToken');
    localStorage.clear();
};
